/* ==========================================================================
   STAND+ mega-menu header controller (section: stand-header)

   Vanilla JS, no jQuery, no theme dependency. Responsibilities:
     - desktop: click to open a mega panel, hover to switch while open,
       Esc / outside click to close
     - mobile: drawer with drill-down screens. The panels themselves are the
       screens, so panel content has a single source of truth in Liquid; this
       file moves the .sh-panels node between the header (desktop) and the
       drawer body (mobile) when the breakpoint changes.
     - "shop by need" switching (identical markup on both breakpoints)
     - predictive search against /search/suggest.json
     - drop countdowns
   The cart drawer and cart counter are the theme's own (js-popup-button),
   so nothing here touches them.
   ========================================================================== */
(function () {
  'use strict';

  var DESKTOP = '(min-width:1200px)';
  // Hover switches (top-level panels, "shop by need") wait this long so a
  // pointer passing over an item on its way somewhere else does not switch.
  var HOVER_INTENT_MS = 200;

  function StandHeader(root) {
    this.root = root;
    this.id = root.getAttribute('data-sh-section-id') || '';
    this.hdr = root.querySelector('[data-sh-hdr]');
    this.panelsWrap = root.querySelector('[data-sh-panels]');
    this.drawer = root.querySelector('[data-sh-drawer]');
    this.drawerBody = this.drawer && this.drawer.querySelector('[data-sh-drawer-body]');
    this.drawerTitle = this.drawer && this.drawer.querySelector('[data-sh-drawer-title]');
    this.drawerBack = this.drawer && this.drawer.querySelector('[data-sh-drawer-back]');
    this.drawerCta = this.drawer && this.drawer.querySelector('[data-sh-drawer-cta]');
    this.rootScreen = this.drawer && this.drawer.querySelector('[data-sh-screen="menu"]');
    this.openBtn = root.querySelector('[data-sh-drawer-open]:not([data-sh-drawer-open="search"])');

    this.panels = {};
    var self = this;
    this.panelList = [].slice.call(root.querySelectorAll('[data-sh-panel]'));
    this.panelList.forEach(function (p) {
      self.panels[p.getAttribute('data-sh-panel')] = p;
    });

    this.triggers = [].slice.call(root.querySelectorAll('[data-sh-menu]'));
    this.openMenu = null;
    this.openedBy = null; // 'click' | 'hover'
    this.stack = [];
    this.mq = window.matchMedia(DESKTOP);
    this.boundHandlers = [];
    this.searchTimer = null;
    this.menuTimer = null;
    this.needTimer = null;
    this.searchAbort = null;
    this.lastQuery = null;

    this.bind();
    this.placePanels();
    this.initNeeds();
    this.initCountdowns();
  }

  /* ---------------------------------------------------------------- utils */

  StandHeader.prototype.on = function (target, type, fn, opts) {
    target.addEventListener(type, fn, opts);
    this.boundHandlers.push([target, type, fn, opts]);
  };

  StandHeader.prototype.destroy = function () {
    this.boundHandlers.forEach(function (h) {
      h[0].removeEventListener(h[1], h[2], h[3]);
    });
    this.boundHandlers = [];
    this.unlockScroll();
  };

  StandHeader.prototype.isDesktop = function () {
    return this.mq.matches;
  };

  /* --------------------------------------------------------------- binding */

  StandHeader.prototype.bind = function () {
    var self = this;

    /* --- desktop triggers --- */
    this.triggers.forEach(function (t) {
      var name = t.getAttribute('data-sh-menu');

      self.on(t, 'click', function (e) {
        e.preventDefault();
        clearTimeout(self.menuTimer);
        if (!self.isDesktop()) {
          // on mobile the same buttons are hidden, but guard anyway
          self.openDrawer(name);
          return;
        }
        // A click only closes a panel the user clicked open. Without this, the
        // mouseenter switch below would have already opened this panel and the
        // click that followed would immediately close it again.
        var close = self.openMenu === name && self.openedBy === 'click';
        self.setMenu(close ? null : name);
        self.openedBy = close ? null : 'click';
      });

      // Once a panel is open, hovering another top-level item switches to it.
      // In "hover" mode, hovering opens from closed as well.
      self.on(t, 'mouseenter', function () {
        if (!self.isDesktop()) return;
        clearTimeout(self.menuTimer);
        self.menuTimer = setTimeout(function () {
          if (self.openMenu && self.openMenu !== name) {
            self.setMenu(name);
            self.openedBy = 'hover';
          } else if (!self.openMenu && self.root.getAttribute('data-sh-open-on') === 'hover') {
            self.setMenu(name);
            self.openedBy = 'hover';
          }
        }, HOVER_INTENT_MS);
      });
      self.on(t, 'mouseleave', function () {
        clearTimeout(self.menuTimer);
      });
    });

    /* --- hover mode closes when the pointer leaves the header --- */
    if (this.hdr && this.root.getAttribute('data-sh-open-on') === 'hover') {
      this.on(this.hdr, 'mouseleave', function () {
        if (self.isDesktop() && self.openMenu) self.setMenu(null);
      });
    }

    /* --- close on outside click / Esc --- */
    this.on(document, 'click', function (e) {
      if (!self.openMenu) return;
      if (self.hdr && self.hdr.contains(e.target)) return;
      self.setMenu(null);
    });

    this.on(document, 'keydown', function (e) {
      if (e.key !== 'Escape' && e.key !== 'Esc') return;
      if (self.openMenu) {
        var was = self.openMenu;
        self.setMenu(null);
        var trigger = self.root.querySelector('[data-sh-menu="' + was + '"]');
        if (trigger) trigger.focus();
        return;
      }
      if (self.drawer && !self.drawer.hasAttribute('hidden')) self.closeDrawer();
    });

    /* --- drawer open buttons --- */
    [].slice.call(this.root.querySelectorAll('[data-sh-drawer-open]')).forEach(function (btn) {
      self.on(btn, 'click', function (e) {
        e.preventDefault();
        var start = btn.getAttribute('data-sh-drawer-open');
        self.openDrawer(start && start !== 'true' ? start : 'menu');
      });
    });

    if (this.drawer) {
      var closeBtn = this.drawer.querySelector('[data-sh-drawer-close]');
      if (closeBtn) this.on(closeBtn, 'click', function () { self.closeDrawer(); });
      if (this.drawerBack) this.on(this.drawerBack, 'click', function () { self.back(); });

      // drill-down inside the drawer
      this.on(this.drawer, 'click', function (e) {
        var go = e.target.closest('[data-sh-go]');
        if (go && self.drawer.contains(go)) {
          e.preventDefault();
          var need = go.getAttribute('data-sh-need');
          var target = go.getAttribute('data-sh-go');
          if (need !== null) self.setNeed(target, need);
          self.push(target);
        }
      });
    }

    /* --- needs switching (works in panel, desktop or drawer) --- */
    this.panelList.forEach(function (panel) {
      var key = panel.getAttribute('data-sh-panel');
      if (!panel.querySelector('[data-sh-need]')) return;

      self.on(panel, 'click', function (e) {
        var btn = e.target.closest('[data-sh-need]');
        if (!btn || !panel.contains(btn)) return;
        e.preventDefault();
        self.setNeed(key, btn.getAttribute('data-sh-need'));
      });

      // desktop convenience: hover (after HOVER_INTENT_MS) / keyboard focus
      // previews a need
      self.on(panel, 'mouseover', function (e) {
        if (!self.isDesktop()) return;
        var btn = e.target.closest('[data-sh-need]');
        if (!btn || !panel.contains(btn)) return;
        // moving between children of the same button is not a new hover
        if (e.relatedTarget && btn.contains(e.relatedTarget)) return;
        clearTimeout(self.needTimer);
        self.needTimer = setTimeout(function () {
          self.setNeed(key, btn.getAttribute('data-sh-need'));
        }, HOVER_INTENT_MS);
      });
      self.on(panel, 'mouseout', function (e) {
        var btn = e.target.closest('[data-sh-need]');
        if (!btn || (e.relatedTarget && btn.contains(e.relatedTarget))) return;
        clearTimeout(self.needTimer);
      });
      self.on(panel, 'focusin', function (e) {
        if (!self.isDesktop()) return;
        var btn = e.target.closest('[data-sh-need]');
        if (!btn || !panel.contains(btn)) return;
        self.setNeed(key, btn.getAttribute('data-sh-need'));
      });
    });

    /* --- search --- */
    [].slice.call(this.root.querySelectorAll('[data-sh-search]')).forEach(function (wrap) {
      self.bindSearch(wrap);
    });

    /* --- breakpoint --- */
    var onChange = function () {
      self.setMenu(null);
      self.placePanels();
      if (self.isDesktop()) self.closeDrawer();
    };
    if (this.mq.addEventListener) this.on(this.mq, 'change', onChange);
    else this.mq.addListener(onChange); // Safari < 14

    /* --- device back gesture closes one drawer level --- */
    this.on(window, 'popstate', function () {
      if (self.drawer && !self.drawer.hasAttribute('hidden')) self.back(true);
    });
  };

  /* ------------------------------------------------------- desktop panels */

  StandHeader.prototype.setMenu = function (name) {
    var self = this;
    clearTimeout(this.menuTimer);
    clearTimeout(this.needTimer);
    this.openMenu = name;
    if (!name) this.openedBy = null;

    this.triggers.forEach(function (t) {
      t.setAttribute('aria-expanded', String(t.getAttribute('data-sh-menu') === name));
    });

    this.panelList.forEach(function (panel) {
      var isOpen = panel.getAttribute('data-sh-panel') === name;
      if (isOpen) panel.removeAttribute('hidden');
      else if (self.isDesktop() || !self.drawer || self.drawer.hasAttribute('hidden')) {
        panel.setAttribute('hidden', '');
      }
    });

    if (name) {
      var panel = this.panels[name];
      var input = panel && panel.querySelector('[data-sh-search-input]');
      if (input) setTimeout(function () { input.focus(); }, 0);
    }
  };

  /* ------------------------------------------------------- panel location */

  /* Desktop: panels live inside <header>. Mobile: they become drawer screens. */
  StandHeader.prototype.placePanels = function () {
    if (!this.panelsWrap || !this.drawerBody || !this.hdr) return;
    var wantedParent = this.isDesktop() ? this.hdr : this.drawerBody;
    if (this.panelsWrap.parentNode !== wantedParent) {
      wantedParent.appendChild(this.panelsWrap);
    }
    if (!this.isDesktop()) this.renderDrawer();
  };

  /* --------------------------------------------------------------- drawer */

  StandHeader.prototype.openDrawer = function (start) {
    if (!this.drawer) return;
    this.setMenu(null);
    this.placePanels();
    this.stack = start && start !== 'menu' && this.panels[start] ? ['menu', start] : ['menu'];
    this.drawer.removeAttribute('hidden');
    this.lockScroll();
    if (this.openBtn) this.openBtn.setAttribute('aria-expanded', 'true');
    this.renderDrawer();
    this.pushState();
    var focusTarget = this.drawer.querySelector('[data-sh-drawer-close]');
    if (focusTarget) focusTarget.focus();
  };

  StandHeader.prototype.closeDrawer = function () {
    if (!this.drawer || this.drawer.hasAttribute('hidden')) return;
    this.drawer.setAttribute('hidden', '');
    this.unlockScroll();
    if (this.openBtn) this.openBtn.setAttribute('aria-expanded', 'false');
    this.stack = [];
  };

  StandHeader.prototype.push = function (screen) {
    if (!this.panels[screen]) return;
    this.stack.push(screen);
    this.renderDrawer();
    this.pushState();
    if (this.drawerBody) this.drawerBody.scrollTop = 0;
  };

  StandHeader.prototype.back = function (fromHistory) {
    var current = this.stack[this.stack.length - 1];

    // inside search, clearing the query is the first "back" step
    if (current && this.panels[current]) {
      var input = this.panels[current].querySelector('[data-sh-search-input]');
      if (input && input.value) {
        input.value = '';
        this.resetSearch(this.panels[current].querySelector('[data-sh-search]'));
        if (!fromHistory) this.pushState();
        return;
      }
    }

    if (this.stack.length > 1) {
      this.stack.pop();
      this.renderDrawer();
      if (this.drawerBody) this.drawerBody.scrollTop = 0;
      if (!fromHistory) this.pushState();
    } else {
      this.closeDrawer();
    }
  };

  StandHeader.prototype.renderDrawer = function () {
    if (!this.drawer) return;
    var current = this.stack[this.stack.length - 1] || 'menu';

    if (this.rootScreen) {
      if (current === 'menu') this.rootScreen.removeAttribute('hidden');
      else this.rootScreen.setAttribute('hidden', '');
    }

    var self = this;
    var activePanel = null;
    this.panelList.forEach(function (panel) {
      if (panel.getAttribute('data-sh-panel') === current) {
        panel.removeAttribute('hidden');
        activePanel = panel;
      } else {
        panel.setAttribute('hidden', '');
      }
    });

    if (this.drawerTitle) {
      this.drawerTitle.textContent = activePanel
        ? activePanel.getAttribute('data-sh-title') || ''
        : this.drawer.getAttribute('data-sh-root-title') || 'Menu';
    }
    if (this.drawerBack) {
      var showBack = this.stack.length > 1;
      this.drawerBack.textContent = showBack ? this.drawerBack.getAttribute('data-sh-back-label') || 'Back' : '';
      this.drawerBack.setAttribute('aria-hidden', String(!showBack));
    }
    if (this.drawerCta) {
      var wantCta = activePanel && activePanel.getAttribute('data-sh-cta') === 'true';
      if (wantCta) this.drawerCta.removeAttribute('hidden');
      else this.drawerCta.setAttribute('hidden', '');
    }

    if (activePanel) {
      var input = activePanel.querySelector('[data-sh-search-input]');
      if (input) setTimeout(function () { input.focus(); }, 60);
    }
  };

  StandHeader.prototype.pushState = function () {
    try {
      history.pushState({ standHeader: this.id || true }, '');
    } catch (e) { /* history unavailable */ }
  };

  StandHeader.prototype.lockScroll = function () {
    this.prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
  };

  StandHeader.prototype.unlockScroll = function () {
    if (this.prevOverflow !== undefined) document.body.style.overflow = this.prevOverflow;
    else document.body.style.overflow = '';
  };

  /* ---------------------------------------------------------------- needs */

  StandHeader.prototype.initNeeds = function () {
    var self = this;
    this.panelList.forEach(function (panel) {
      if (!panel.querySelector('[data-sh-view]')) return;
      var def = panel.getAttribute('data-sh-default-view');
      var views = [].slice.call(panel.querySelectorAll('[data-sh-view]'));
      var exists = views.some(function (v) {
        return v.getAttribute('data-sh-view') === def;
      });
      if (def === null || !exists) {
        def = views.length ? views[0].getAttribute('data-sh-view') : '';
      }
      self.setNeed(panel.getAttribute('data-sh-panel'), def);
    });
  };

  StandHeader.prototype.setNeed = function (panelKey, needId) {
    var panel = this.panels[panelKey];
    if (!panel) return;

    [].slice.call(panel.querySelectorAll('[data-sh-view]')).forEach(function (view) {
      var on = view.getAttribute('data-sh-view') === needId;
      if (on) view.removeAttribute('hidden');
      else view.setAttribute('hidden', '');
    });

    [].slice.call(panel.querySelectorAll('[data-sh-need]')).forEach(function (btn) {
      var on = btn.getAttribute('data-sh-need') === needId;
      btn.classList.toggle('is-on', on);
      if (btn.hasAttribute('aria-pressed')) btn.setAttribute('aria-pressed', String(on));
    });
  };

  /* --------------------------------------------------------------- search */

  StandHeader.prototype.bindSearch = function (wrap) {
    var self = this;
    var input = wrap.querySelector('[data-sh-search-input]');
    var form = wrap.querySelector('form');
    if (!input) return;

    this.on(input, 'input', function () {
      var q = input.value.trim();
      clearTimeout(self.searchTimer);
      if (!q) {
        self.resetSearch(wrap);
        return;
      }
      self.searchTimer = setTimeout(function () {
        self.runSearch(wrap, q);
      }, 280);
    });

    // canned suggestions
    this.on(wrap, 'click', function (e) {
      var btn = e.target.closest('[data-sh-q]');
      if (!btn || !wrap.contains(btn)) return;
      e.preventDefault();
      var q = btn.getAttribute('data-sh-q');
      input.value = q;
      self.runSearch(wrap, q);
      input.focus();
    });

    var reset = wrap.querySelector('[data-sh-search-reset]');
    if (reset) {
      this.on(reset, 'click', function (e) {
        e.preventDefault();
        input.value = '';
        self.resetSearch(wrap);
        input.focus();
      });
    }

    if (form) {
      this.on(form, 'submit', function (e) {
        if (!input.value.trim()) e.preventDefault();
      });
    }
  };

  StandHeader.prototype.resetSearch = function (wrap) {
    if (!wrap) return;
    var def = wrap.querySelector('[data-sh-search-default]');
    var res = wrap.querySelector('[data-sh-search-results]');
    if (def) def.removeAttribute('hidden');
    if (res) {
      res.setAttribute('hidden', '');
      res.innerHTML = '';
    }
    this.lastQuery = null;
    if (this.searchAbort) {
      this.searchAbort.abort();
      this.searchAbort = null;
    }
  };

  StandHeader.prototype.runSearch = function (wrap, q) {
    var self = this;
    var def = wrap.querySelector('[data-sh-search-default]');
    var res = wrap.querySelector('[data-sh-search-results]');
    if (!res) return;
    if (this.lastQuery === q) return;
    this.lastQuery = q;

    if (def) def.setAttribute('hidden', '');
    res.removeAttribute('hidden');
    if (!res.innerHTML) {
      res.innerHTML = '<p class="sh-search-status">' + this.t(wrap, 'loading', 'Searching…') + '</p>';
    }

    if (this.searchAbort) this.searchAbort.abort();
    var controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    this.searchAbort = controller;

    var limit = parseInt(wrap.getAttribute('data-sh-search-limit'), 10) || 6;
    var base = wrap.getAttribute('data-sh-search-url') || '/search';
    var url =
      base.replace(/\/$/, '') +
      '/suggest.json?q=' +
      encodeURIComponent(q) +
      '&resources[type]=product,collection,page&resources[limit]=' +
      limit +
      '&resources[options][unavailable_products]=last&resources[options][fields]=title,product_type,variants.title,vendor';

    fetch(url, controller ? { signal: controller.signal } : undefined)
      .then(function (r) {
        if (!r.ok) throw new Error('search failed');
        return r.json();
      })
      .then(function (data) {
        if (self.lastQuery !== q) return;
        res.innerHTML = self.renderResults(wrap, q, data);
      })
      .catch(function (err) {
        if (err && err.name === 'AbortError') return;
        res.innerHTML =
          '<p class="sh-search-status">' +
          self.t(wrap, 'error', 'Search is unavailable right now.') +
          ' <a class="sh-search-more" href="' +
          (wrap.getAttribute('data-sh-search-url') || '/search') +
          '?q=' +
          encodeURIComponent(q) +
          '">' +
          self.t(wrap, 'all', 'See all results') +
          '</a></p>';
      });
  };

  StandHeader.prototype.t = function (wrap, key, fallback) {
    return wrap.getAttribute('data-sh-text-' + key) || fallback;
  };

  StandHeader.prototype.money = function (wrap, value) {
    if (value === null || value === undefined || value === '') return '';
    var num = typeof value === 'number' ? value : parseFloat(String(value).replace(/[^0-9.\-]/g, ''));
    if (isNaN(num)) return String(value);
    var currency = wrap.getAttribute('data-sh-currency') || 'USD';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: num % 1 === 0 ? 0 : 2,
      }).format(num);
    } catch (e) {
      return num.toFixed(2);
    }
  };

  /* Same colourway-suffix cleanup the Liquid cards do, so predictive results
     and panel cards show the same product names. */
  StandHeader.prototype.cleanTitle = function (wrap, title) {
    var strip = wrap.getAttribute('data-sh-strip-title');
    var out = String(title || '');
    if (!strip) return out.trim();
    strip.split('|').forEach(function (part) {
      var needle = part.trim();
      if (needle) out = out.split(needle).join('');
    });
    return out.trim();
  };

  StandHeader.prototype.esc = function (s) {
    return String(s === null || s === undefined ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  };

  StandHeader.prototype.img = function (url, size) {
    if (!url) return '';
    return String(url).replace(/(\.(?:png|jpe?g|webp|gif))(\?|$)/i, '_' + size + '$1$2');
  };

  StandHeader.prototype.renderResults = function (wrap, q, data) {
    var self = this;
    var r = (data && data.resources && data.resources.results) || {};
    var products = r.products || [];
    var pages = (r.pages || []).concat(r.collections || []);
    var searchUrl = wrap.getAttribute('data-sh-search-url') || '/search';
    var allHref = searchUrl + '?q=' + encodeURIComponent(q) + '&options[prefix]=last';

    if (!products.length && !pages.length) {
      return (
        '<p class="sh-search-status">' +
        self.t(wrap, 'empty', 'No matches for') +
        ' “' +
        self.esc(q) +
        '”. <a class="sh-search-more" href="' +
        allHref +
        '">' +
        self.t(wrap, 'all', 'See all results') +
        '</a></p>'
      );
    }

    var html = '';
    if (products.length) {
      html +=
        '<span class="sh-lbl sh-results-head">' +
        products.length +
        ' ' +
        self.t(wrap, 'matches', 'products match') +
        ' “' +
        self.esc(q) +
        '”</span>';
      html += '<div class="sh-results">';
      products.forEach(function (p) {
        var image = p.featured_image && p.featured_image.url ? p.featured_image.url : p.image;
        html +=
          '<a class="sh-alt" href="' +
          self.esc(p.url) +
          '">' +
          '<div class="sh-thumb" style="height:72px' +
          (image ? ";background-image:url('" + self.esc(self.img(image, '200x')) + "')" : '') +
          '"></div>' +
          '<span class="sh-alt__txt">' +
          '<strong class="sh-alt__name">' +
          self.esc(self.cleanTitle(wrap, p.title)) +
          '</strong>' +
          (p.price !== undefined
            ? '<span class="sh-alt__why">' + self.esc(self.money(wrap, p.price)) + '</span>'
            : '') +
          '</span></a>';
      });
      html += '</div>';
    }

    if (pages.length) {
      html += '<div class="sh-links sh-links--bordered">';
      pages.forEach(function (p) {
        html += '<a href="' + self.esc(p.url) + '">' + self.esc(p.title) + '</a>';
      });
      html += '</div>';
    }

    html += '<a class="sh-search-more" href="' + allHref + '">' + self.t(wrap, 'all', 'See all results') + ' →</a>';
    return html;
  };

  /* ------------------------------------------------------------ countdown */

  StandHeader.prototype.initCountdowns = function () {
    var self = this;
    var nodes = [].slice.call(this.root.querySelectorAll('[data-sh-countdown]'));
    if (!nodes.length) return;

    var tick = function () {
      nodes.forEach(function (node) {
        var target = Date.parse(node.getAttribute('data-sh-countdown'));
        if (isNaN(target)) return;
        var diff = Math.max(0, target - Date.now());
        var totalSeconds = Math.floor(diff / 1000);
        var parts = {
          days: Math.floor(totalSeconds / 86400),
          hours: Math.floor((totalSeconds % 86400) / 3600),
          mins: Math.floor((totalSeconds % 3600) / 60),
          secs: totalSeconds % 60,
        };
        Object.keys(parts).forEach(function (key) {
          var el = node.querySelector('[data-sh-cd="' + key + '"]');
          if (el) el.textContent = String(parts[key]).padStart(2, '0');
        });
      });
    };

    tick();
    var interval = setInterval(tick, 1000);
    this.boundHandlers.push([
      { removeEventListener: function () { clearInterval(interval); } },
      'noop',
      function () {},
      undefined,
    ]);
  };

  /* ------------------------------------------------------------ lifecycle */

  var instances = {};

  function init(scope) {
    var roots = [].slice.call((scope || document).querySelectorAll('[data-sh-root]'));
    roots.forEach(function (root) {
      var id = root.getAttribute('data-sh-section-id') || 'default';
      if (instances[id] && instances[id].root === root) return;
      if (instances[id]) instances[id].destroy();
      instances[id] = new StandHeader(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  /* Shopify theme editor: re-init a reloaded section, and open the panel the
     merchant is editing so the content they picked is visible. */
  document.addEventListener('shopify:section:load', function (e) {
    if (!e.target || !e.target.querySelector('[data-sh-root]')) return;
    init(e.target);
  });

  document.addEventListener('shopify:section:unload', function (e) {
    if (!e.target) return;
    var root = e.target.querySelector('[data-sh-root]');
    if (!root) return;
    var id = root.getAttribute('data-sh-section-id') || 'default';
    if (instances[id]) {
      instances[id].destroy();
      delete instances[id];
    }
  });

  function revealBlock(blockEl) {
    if (!blockEl) return;
    var rootEl = blockEl.closest('[data-sh-root]');
    if (!rootEl) return;
    var inst = instances[rootEl.getAttribute('data-sh-section-id') || 'default'];
    if (!inst) return;

    // a content block sits inside its panel; a nav item names one
    var panel = blockEl.closest('[data-sh-panel]');
    var key = panel
      ? panel.getAttribute('data-sh-panel')
      : blockEl.getAttribute('data-sh-menu') ||
        (blockEl.querySelector('[data-sh-menu]') &&
          blockEl.querySelector('[data-sh-menu]').getAttribute('data-sh-menu'));
    if (!key) return;

    if (inst.isDesktop()) inst.setMenu(key);
    else inst.openDrawer(key);

    // a need block selected in the editor should also show its own view
    var needBtn = blockEl.matches('[data-sh-need]')
      ? blockEl
      : blockEl.querySelector('[data-sh-need]');
    var viewEl = blockEl.matches('[data-sh-view]') ? blockEl : blockEl.querySelector('[data-sh-view]');
    if (viewEl) inst.setNeed(key, viewEl.getAttribute('data-sh-view'));
    else if (needBtn) inst.setNeed(key, needBtn.getAttribute('data-sh-need'));
  }

  document.addEventListener('shopify:block:select', function (e) {
    revealBlock(e.target);
  });

  document.addEventListener('shopify:block:deselect', function (e) {
    var rootEl = e.target && e.target.closest('[data-sh-root]');
    if (!rootEl) return;
    var inst = instances[rootEl.getAttribute('data-sh-section-id') || 'default'];
    if (inst && inst.isDesktop()) inst.setMenu(null);
  });

  window.StandHeader = StandHeader;
})();
